import type { FeatureFlag } from '@/features/feature-flags/domain/feature-flags'

// Client-side-only overrides, set from the dev-tools panel (Cmd+D → Shift+F).
// 'unchanged' is the default and is never persisted — it means "read the flag
// from the backend normally". A forced value wins over both
// VITE_FEATURE_OVERRIDES and the API response until set back to 'unchanged'.
export type FeatureFlagOverride = 'true' | 'false' | 'unchanged'

export type ForcedFeatureFlagOverrides = Partial<Record<FeatureFlag, 'true' | 'false'>>

export const FEATURE_FLAG_OVERRIDES_STORAGE_KEY = 'scope.devTools.featureFlags.v1'

function readStorage(): ForcedFeatureFlagOverrides {
  try {
    const raw = localStorage.getItem(FEATURE_FLAG_OVERRIDES_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const overrides: ForcedFeatureFlagOverrides = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (value === 'true' || value === 'false') {
        overrides[key as FeatureFlag] = value
      }
    }
    return overrides
  } catch {
    return {}
  }
}

function writeStorage(overrides: ForcedFeatureFlagOverrides) {
  try {
    localStorage.setItem(FEATURE_FLAG_OVERRIDES_STORAGE_KEY, JSON.stringify(overrides))
  } catch {
    // Storage unavailable (private mode, quota) — overrides stay in-memory
    // for the session instead of breaking the dev-tools panel.
  }
}

function notify() {
  for (const listener of listeners) listener()
}

// Identity-stable snapshot: replaced on every write so useSyncExternalStore
// re-renders consumers and never sees tearing between writes.
let snapshot: ForcedFeatureFlagOverrides = readStorage()
const listeners = new Set<() => void>()

export function subscribeFeatureFlagOverrides(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getFeatureFlagOverrides(): ForcedFeatureFlagOverrides {
  return snapshot
}

export function getFeatureFlagOverride(flag: FeatureFlag): FeatureFlagOverride {
  return snapshot[flag] ?? 'unchanged'
}

export function setFeatureFlagOverride(flag: FeatureFlag, override: FeatureFlagOverride) {
  const next: ForcedFeatureFlagOverrides = { ...snapshot }
  if (override === 'unchanged') {
    delete next[flag]
  } else {
    next[flag] = override
  }
  writeStorage(next)
  snapshot = next
  notify()
}

// Bulk replace — the dev-tools panel stages edits in a draft and applies them
// in one write on Save, so consumers re-render once.
export function setFeatureFlagOverrides(overrides: ForcedFeatureFlagOverrides) {
  const next: ForcedFeatureFlagOverrides = { ...overrides }
  writeStorage(next)
  snapshot = next
  notify()
}
